import React, { useEffect, useState } from 'react';
import { useGetUserList } from '../graphql/useGetUserList';
import {
  AdvancedTable,
  Alert,
  type AlertDetails,
  Button,
  Dialog,
  type HeadCell,
  Loading,
  pageRowsSelectOptions,
  SearchField,
} from '../../../../components';
import { type SuperAdminUser } from '../graphql/types';
import styled from 'styled-components';
import { Chip, Stack } from '@mui/material';
import { useUpdateUserRole } from '../graphql/useUpdateUserRole';
import { CreateUserRoleDialog } from './CreateUserRoleDialog';
import { CreateUserDialog } from './CreateUserDialog';

export const UserList: React.FC = () => {
  const [page, setPage] = useState(1); // default current page is 1
  const [pageCount, setPageCount] = useState(1); // default page count number is 1
  const [recordsCount, setRecordsCount] = useState(0);
  const [selectRowsLimit, setSelectRowsLimit] = useState(
    pageRowsSelectOptions[0].value as number, // default rows per page is the first option
  );
  const [stashedSearchContent, setStashedSearchContent] = useState<string>('');
  const [searchContent, setSearchContent] = useState<string>('');

  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] = useState<AlertDetails>({
    alertType: 'success',
    alertContent: '',
  });

  const [addUserDialogOpen, setAddUserDialogOpen] = React.useState(false);
  const [modRoleDialogOpen, setModRoleDialogOpen] = React.useState(false);
  const [addRole, setAddRole] = React.useState('');
  const [currentRow, setCurrentRow] = React.useState<{
    netId?: string;
    role?: string[];
  }>({});

  const { users, total, loading, refetch } = useGetUserList({
    pagination: {
      number: page,
      size: selectRowsLimit,
    },
    filter: searchContent ? { netId: searchContent } : undefined,
  });
  const updateAdvancedUser = useUpdateUserRole();

  useEffect(() => {
    if (users != null) {
      setRecordsCount(total);
    } else {
      setRecordsCount(0);
    }
    setPageCount(Math.floor(total / selectRowsLimit));
  }, [users]);

  const handleDeleteRole = async (role: string, row: any): Promise<void> => {
    void updateAdvancedUser({
      variables: {
        input: {
          netId: row.netId,
          [role]: false,
        },
      },
    });
  };

  const handleAddRole = async (role: string, row: any): Promise<void> => {
    void updateAdvancedUser({
      variables: {
        input: {
          netId: row.netId,
          [role]: true,
        },
      },
    });
  };

  const handleRoleDisplay = (role: string[], row?: any): React.ReactNode => {
    return (
      <Stack direction="row" flexWrap="wrap">
        {role.map((r, index) => {
          return (
            <Chip
              key={index}
              onDelete={async () => {
                void handleDeleteRole(r, row);
                refetch();
              }}
              label={r}
              component="div"
            />
          );
        })}
        <Button
          onClick={async () => {
            setCurrentRow(row);
            setModRoleDialogOpen(true);
          }}
        >
          +
        </Button>
      </Stack>
    );
  };

  const handleSearchApplication = (search: string): void => {
    // TODO: until the releasing status enum is ready
  };

  // const handleRequestSort = (property: keyof SuperAdminUser): void => {
  //   // TODO: Get the sorted data from the server
  // };

  const handleChangePage = (event: unknown, newPage: number): void => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setSelectRowsLimit(parseInt(event.target.value));
    setPage(1);
  };

  const handleContentChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setSearchContent(event.target.value);
  };

  const headCells: Array<HeadCell<SuperAdminUser & { action?: any }>> = [
    {
      id: 'department',
      label: 'Department',
      isSortable: false,
      render: (row: any) => row.department,
    },
    {
      id: 'netId',
      label: 'NetID',
      isSortable: false,
      render: (row: any) => row.netId,
    },
    {
      id: 'displayName',
      label: 'Name',
      isSortable: false,
      render: (row: any) => row.name,
    },
    {
      id: 'email',
      label: 'Email',
      isSortable: false,
      render: (row: any) => row.email,
    },
    {
      id: 'role',
      label: 'Role',
      isSortable: false,
      render: (row: any) => handleRoleDisplay(row.role, row),
    },
    // {
    //   id: 'action',
    //   label: 'Action',
    //   isSortable: false,
    //   minWidth:10,
    //   render: (row: any) => row.action,
    // },
    {
      id: 'updatedAt',
      label: 'Updated At',
      isSortable: false,
      render: (row: any) => {
        return new Date(row.updatedAt).toLocaleString('zh-CN');
      },
    },
    {
      id: 'updatedBy',
      label: 'Updated By',
      isSortable: false,
      render: (row: any) => row.updatedBy,
    },
  ];

  return loading ? (
    <Loading />
  ) : (
    <TableStyle>
      <Alert
        openAlert={openAlert}
        alertDetails={alertDetails}
        setOpenAlert={setOpenAlert}
      />
      <Dialog
        openDialog={modRoleDialogOpen}
        dialogTitle="Add Role"
        onDialogAction={() => {
          void handleAddRole(addRole, currentRow);
          setModRoleDialogOpen(false);
          refetch();
        }}
        onDialogClose={() => {
          setModRoleDialogOpen(false);
        }}
        dialogAction="Add"
        cancelAction="Cancel"
      >
        <CreateUserRoleDialog
          netId={currentRow?.netId}
          role={currentRow?.role}
          setAddRole={setAddRole}
        />
      </Dialog>
      <CreateUserDialog
        open={addUserDialogOpen}
        onClose={(success?: boolean) => {
          if (success != null) {
            setAlertDetails(
              success
                ? {
                    alertType: 'success',
                    alertContent: 'User created successfully.',
                  }
                : {
                    alertType: 'error',
                    alertContent:
                      'User creation failed. Please check if the user has been granted with access, if yes, please change the rights only.',
                  },
            );
            setOpenAlert(true);
          }
          setAddUserDialogOpen(false);
        }}
      />

      <SearchFieldStyle>
        <SearchField
          content={stashedSearchContent}
          placeholder="NetID"
          handleContentChange={e => {
            setStashedSearchContent(e.target.value);
          }}
          handleSearch={() => {
            setSearchContent(stashedSearchContent);
            refetch();
          }}
        />
        <Button
          variant="outlined"
          onClick={() => {
            setAddUserDialogOpen(true);
          }}
        >
          Create Default User
        </Button>
      </SearchFieldStyle>

      <AdvancedTable<SuperAdminUser>
        tableProps={{
          headCells,
          data: users ?? [],
          handleRequestSort: () => {},
        }}
        advancedPaginationProps={{
          page,
          count: pageCount,
          recordText: `${recordsCount} records`,
          rowsPerPageOptions: pageRowsSelectOptions,
          rowsPerPage: selectRowsLimit,
          onChangeRowsPerPage: handleChangeRowsPerPage,
        }}
        searchFieldProps={{
          placeholder: 'Application No.',
          content: searchContent,
          handleContentChange,
          handleSearch: handleSearchApplication,
        }}
        handleChangePage={handleChangePage}
      />
    </TableStyle>
  );
};

const TableStyle = styled.div`
  min-width: 1200px;
`;

const SearchFieldStyle = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  margin-bottom: 20px;
`;
